
// suntimes.models.ts
export interface CalendarT {
  year: number;   // 0..99 (si tu C usa año relativo a 2000) o año completo; ver abajo
  month: number;  // 1..12
  date: number;   // 1..31
}

export interface CoordT {
  Grados: number;   // grados enteros
  Minutos: number;
  Segundos: number;
  Signo?: number;   // -1 para W/S si lo usas (como en C)
}

export type CoordInput = CoordT | number; // acepta decimal (lat/lng) o DMS

export interface SuntimesResult {
  sunriseMin: number | null; // minutos desde 00:00 local, null si OOB
  sunsetMin: number | null;
  isAllDay: boolean;
  isAllNight: boolean;
}

export interface SuntimesLegacyDayRow extends SuntimesResult {
  date: Date;
  weekday: string;
  sunriseStr: string;
  sunsetStr: string;
}

// suntimes.service.ts
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class SuntimesService {
  // Constantes típicas (AJUSTA a tus defines del firmware si las tienes)
  private readonly PI = Math.PI;
  private readonly RAD = 180 / Math.PI; // en tu C: multiplican por RAD para pasar rad->deg
  // Ojo: en tu C parece usar RAD como 180/pi (rad->deg), y dividir por RAD para deg->rad.

  // Estas constantes NO salen en el snippet; son parte del algoritmo original.
  // Pon aquí exactamente las del proyecto C si las tienes:
  // private readonly T0 = 2415020.5;
  private readonly T0 = 2444000.5;
  private readonly VP = 8.22e-5;         // <-- AJUSTAR
  private readonly M0 = 2.12344;         // <-- AJUSTAR
  private readonly MN = 1.72019e-2;      // <-- AJUSTAR
  private readonly F = 86400 / (2 * Math.PI); // típico factor seg/rad; AJUSTAR si en C es otro
  private readonly MR = 0.04301;        // <-- AJUSTAR

  // Límites/valores especiales
  private readonly MAX_SUNTIME_LIMIT = 24 * 60 + 59; // 1439
  private readonly SUNTIME_OOB = null;

  calculateSuntimesLegacy(
    cal: CalendarT,
    gmtMinutes: number,        // en tu C: int16_t gmt (minutos)
    lat: CoordInput,
    lng: CoordInput,
    corrSunrise = 0,           // si quieres aplicar correcciones extra (min)
    corrSunset = 0
  ): SuntimesResult {
    let isAllDay = false;
    let isAllNight = false;

    // Nota: 0 es un valor válido para lat/lng (aunque sea falsy en JS),
    // por lo que aquí solo debemos rechazar null/undefined.
    if (!cal || lat == null || lng == null) {
      throw new Error('calculateSuntimes: parámetros inválidos');
    }

    // bugfix de tu código: usar 2016 (bisiesto) o 2017 (no bisiesto)
    // En tu C: year = cal->year; y luego TABLES_YEAR_LOW() / -1.
    // Aquí asumo que cal.year es el año completo (ej 2026).
    // Si cal.year es "26" (relativo 2000), conviértelo: fullYear = 2000 + cal.year
    const fullYear = cal.year < 100 ? 2000 + cal.year : cal.year;
    const yearForCalc = (fullYear % 4 === 0) ? 2016 : 2017;

    const Lon = this.coordToDecimal(lng);
    const Lat = this.coordToDecimal(lat);

    // --- JULIAN DAY (igual que tu C, manteniendo estructura) ---
    const GGG = 1; // tu C lo fija a 1

    let JD = -1 * Math.floor(7 * (Math.floor((cal.month + 9) / 12) + yearForCalc) / 4);

    let P = 1;
    if ((cal.month - 9) < 0) P = -1;

    const A = Math.abs(cal.month - 9);
    let J1 = Math.floor(yearForCalc + P * Math.floor(A / 7));
    J1 = -1 * Math.floor((Math.floor(J1 / 100) + 1) * 3 / 4);

    JD = JD + Math.floor(275 * cal.month / 9) + cal.date + (GGG * J1);
    JD = JD + 1721027 + 2 * GGG + 367 * yearForCalc - 0.5;

    // --- Astronomía ---
    let PP = 4.93204;
    let ET = 0.016718;

    PP = PP + (JD - this.T0) * this.VP / 100;

    let AM = this.M0 + this.MN * (JD - this.T0);
    AM = AM - 2 * this.PI * Math.floor(AM / (2 * this.PI));

    let V = AM + 2 * ET * Math.sin(AM) + 1.25 * ET * ET * Math.sin(2 * AM);
    if (V < 0) V = 2 * this.PI + V;

    let L = PP + V;
    L = L - 2 * this.PI * Math.floor(L / (2 * this.PI));

    let DC = (JD - 2415020.5) / 365.2422;

    // O_B (oblicuidad)
    let O_B = 23.452294 - (0.46845 * DC + 0.00000059 * DC * DC) / 3600;
    // deg -> rad (en C: O_B = O_B/RAD; donde RAD=180/pi)
    O_B = O_B / this.RAD;

    // declinación solar
    DC = Math.asin(Math.sin(O_B) * Math.sin(L));

    // ascensión recta (AR)
    let AR = Math.acos(Math.cos(L) / Math.cos(DC));
    if (L > this.PI) AR = 2 * this.PI - AR;

    // de vuelta a grados (en C: *RAD)
    O_B = O_B * this.RAD;
    L = L * this.RAD;
    AR = AR * 12 / this.PI;
    DC = DC * this.RAD;

    // ecuación del tiempo
    AR = 2 * ET * this.F * Math.sin(AM) + 1.25 * ET * ET * this.F * Math.sin(2 * AM);
    let H0 = -this.MR * this.F * Math.sin(2 * (PP + AM)) + (this.MR * this.MR * this.F * Math.sin(4 * (PP + AM))) / 2;
    ET = AR + H0;

    // ángulo horario base
    H0 = Math.acos(-Math.tan(Lat / this.RAD) * Math.tan(DC / this.RAD));
    H0 = H0 * this.RAD;

    const VD = 0.9856 * Math.sin(O_B / this.RAD) * Math.cos(L / this.RAD) / Math.cos(DC / this.RAD);

    // --- Orto ---
    let DCOR = VD * (-H0 + 180) / 360;
    DCOR += DC;

    let HORTO = -Math.acos(-Math.tan(Lat / this.RAD) * Math.tan(DCOR / this.RAD));
    let HOR = 5 / (6 * Math.cos(Lat / this.RAD) * Math.cos(DCOR / this.RAD) * Math.sin(HORTO));
    HORTO = (HORTO * this.RAD + HOR) / 15;

    let TUC = HORTO + ET / 3600 - Lon / 15 + 12;

    let horOrto = Math.floor(TUC);
    let minOrto = Math.floor((TUC - horOrto) * 60 + 0.5);
    if (minOrto > 59) { minOrto -= 60; horOrto += 1; }

    // --- Ocaso ---
    const VDOC = VD * (H0 + 180) / 360;
    const DCOC = DC + VDOC;

    let HOC = Math.acos(-Math.tan(Lat / this.RAD) * Math.tan(DCOC / this.RAD));
    let VHOC = 5 / (6 * Math.cos(Lat / this.RAD) * Math.cos(DCOC / this.RAD) * Math.sin(HOC));
    HOC = (HOC * this.RAD + VHOC) / 15;

    let TUOC = HOC + ET / 3600 - Lon / 15 + 12;

    let horOcaso = Math.floor(TUOC);
    let minOcaso = Math.floor((TUOC - horOcaso) * 60 + 0.5);
    if (minOcaso > 59) { minOcaso -= 60; horOcaso += 1; }

    // --- Validaciones estilo IsNaN + flags polar day/night ---
    const sunriseInvalid = this.isInvalidTime(horOrto, minOrto, gmtMinutes);
    const sunsetInvalid  = this.isInvalidTime(horOcaso, minOcaso, gmtMinutes);

    let sunriseMin: number | null = this.SUNTIME_OOB;
    let sunsetMin: number | null = this.SUNTIME_OOB;

    if (sunriseInvalid) {
      // tu C marca allNight en invierno (meses fuera mar-oct), allDay en verano
      if (cal.month < 3 || cal.month > 10) isAllNight = true;
      else { isAllDay = true; sunsetMin = this.SUNTIME_OOB; }
    } else {
      sunriseMin = horOrto * 60 + minOrto + gmtMinutes + corrSunrise;
      sunriseMin = this.normalizeMinutes(sunriseMin);
      if (sunriseMin == null) sunriseMin = this.SUNTIME_OOB;
    }

    if (sunsetInvalid) {
      if (isAllNight) {
        sunsetMin = 0; // tu C hace 0 si allNight
      } else {
        sunsetMin = this.SUNTIME_OOB;
      }
    } else {
      sunsetMin = horOcaso * 60 + minOcaso + gmtMinutes + corrSunset;
      // en tu C: si pasa el límite y "salta" más allá del sunrise => allDay
      if (sunsetMin > this.MAX_SUNTIME_LIMIT && sunriseMin != null && (sunsetMin - this.MAX_SUNTIME_LIMIT) >= sunriseMin) {
        isAllDay = true;
        sunsetMin = this.SUNTIME_OOB;
      } else {
        sunsetMin = this.normalizeMinutes(sunsetMin) ?? this.SUNTIME_OOB;
      }
    }

    return { sunriseMin, sunsetMin, isAllDay, isAllNight };
  }

  /**
   * Calcula orto/ocaso para el día indicado y los próximos 6 días (7 en total).
   * Devuelve también strings HH:MM y el nombre del día localizado.
   */
  calculateSuntimesLegacyForWeek(
    start: CalendarT | Date,
    gmtMinutes: number,
    lat: CoordInput,
    lng: CoordInput,
    locale = 'es-ES',
    corrSunrise = 0,
    corrSunset = 0
    ): SuntimesLegacyDayRow[] {
    const startDate = this.toDate(start);
    const out: SuntimesLegacyDayRow[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);

      const cal = this.calendarFromDate(d);
      const res = this.calculateSuntimesLegacy(cal, gmtMinutes, lat, lng, corrSunrise, corrSunset);

      out.push({
        ...res,
        date: d,
        weekday: this.weekdayName(d, locale),
        sunriseStr: this.minutesToHHMM(res.sunriseMin),
        sunsetStr: this.minutesToHHMM(res.sunsetMin),
      });
    }

    return out;
  }

  /**
   * Calcula orto/ocaso para todos los días del año indicado (365 o 366 días).
   * Devuelve el mismo formato que calculateSuntimesLegacyForWeek.
   */
  calculateSuntimesLegacyForYear(
    year: number,
    gmtMinutes: number,
    lat: CoordInput,
    lng: CoordInput,
    locale = 'es-ES',
    corrSunrise = 0,
    corrSunset = 0
  ): SuntimesLegacyDayRow[] {
    const out: SuntimesLegacyDayRow[] = [];
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const totalDays = isLeap ? 366 : 365;

    const startDate = new Date(year, 0, 1); // 1 de enero

    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);

      const cal = this.calendarFromDate(d);
      const res = this.calculateSuntimesLegacy(cal, gmtMinutes, lat, lng, corrSunrise, corrSunset);

      out.push({
        ...res,
        date: d,
        weekday: this.weekdayName(d, locale),
        sunriseStr: this.minutesToHHMM(res.sunriseMin),
        sunsetStr: this.minutesToHHMM(res.sunsetMin),
      });
    }

    return out;
  }

  /**
   * Calcula orto/ocaso para todos los días del año usando un offset GMT distinto por día.
   * Útil para aplicar el cambio de estación en las fechas configuradas del dispositivo.
   */
  calculateSuntimesLegacyForYearWithGmtResolver(
    year: number,
    resolveGmtMinutes: (date: Date) => number,
    lat: CoordInput,
    lng: CoordInput,
    locale = 'es-ES',
    corrSunrise = 0,
    corrSunset = 0
  ): SuntimesLegacyDayRow[] {
    const out: SuntimesLegacyDayRow[] = [];
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    const totalDays = isLeap ? 366 : 365;
    const startDate = new Date(year, 0, 1);

    for (let i = 0; i < totalDays; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);

      const cal = this.calendarFromDate(d);
      const gmtMinutes = resolveGmtMinutes(d);
      const res = this.calculateSuntimesLegacy(cal, gmtMinutes, lat, lng, corrSunrise, corrSunset);

      out.push({
        ...res,
        date: d,
        weekday: this.weekdayName(d, locale),
        sunriseStr: this.minutesToHHMM(res.sunriseMin),
        sunsetStr: this.minutesToHHMM(res.sunsetMin),
      });
    }

    return out;
  }

  /**
   * Calcula orto/ocaso para 7 días consecutivos desde la fecha indicada,
   * aplicando un offset GMT distinto por día.
   */
  calculateSuntimesLegacyForWeekWithGmtResolver(
    start: CalendarT | Date,
    resolveGmtMinutes: (date: Date) => number,
    lat: CoordInput,
    lng: CoordInput,
    locale = 'es-ES',
    corrSunrise = 0,
    corrSunset = 0
  ): SuntimesLegacyDayRow[] {
    const startDate = this.toDate(start);
    const out: SuntimesLegacyDayRow[] = [];

    for (let i = 0; i < 7; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);

      const cal = this.calendarFromDate(d);
      const gmtMinutes = resolveGmtMinutes(d);
      const res = this.calculateSuntimesLegacy(cal, gmtMinutes, lat, lng, corrSunrise, corrSunset);

      out.push({
        ...res,
        date: d,
        weekday: this.weekdayName(d, locale),
        sunriseStr: this.minutesToHHMM(res.sunriseMin),
        sunsetStr: this.minutesToHHMM(res.sunsetMin),
      });
    }

    return out;
  }

  private coordToDecimal(c: CoordInput): number {
    if (typeof c === 'number') {
      return c;
    }

    const sign = (c.Signo === -1 || c.Grados < 0) ? -1 : 1;
    const absDeg = Math.abs(c.Grados) + (c.Minutos / 60) + (c.Segundos / 3600);
    return sign * absDeg;
  }

  private isInvalidTime(h: number, m: number, gmtMinutes: number): boolean {
    // emula tu IsNaN(...) + sanity
    if (!Number.isFinite(h) || !Number.isFinite(m)) return true;
    if (h < -48 || h > 72) return true; // rangos amplios para capturar casos raros
    if (m < 0 || m > 59) return true;
    if (!Number.isFinite(gmtMinutes)) return true;
    return false;
  }

  private normalizeMinutes(total: number): number | null {
    // devuelve minutos 0..1439, o null si fuera de rango “razonable”
    if (!Number.isFinite(total)) return null;
    // opcional: permitir wrap 24h
    total = ((total % 1440) + 1440) % 1440;
    return total;
  }

  private calendarFromDate(date: Date): CalendarT {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      date: date.getDate(),
    };
  }

  private toDate(input: CalendarT | Date): Date {
    if (input instanceof Date) return new Date(input);
    const fullYear = input.year < 100 ? 2000 + input.year : input.year;
    return new Date(fullYear, input.month - 1, input.date);
  }

  private weekdayName(date: Date, locale = 'es-ES'): string {
    return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
  }

  private minutesToHHMM(m: number | null): string {
    if (m == null) return '--:--';
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  }
}
